import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* Members arrive in the same shape as /myProfile users — flatten what the list
   UI needs, tolerating flatter shapes too. */
/* Kids arrive either as plain name strings or as childData objects
   ({ id, childName, birthDate, gender, imagePath }). birthDate is a full ISO
   timestamp — trim to yyyy-mm-dd so fmtDate/kidAge (which append T00:00:00) work. */
function normalizeKid(k) {
  if (typeof k === 'string') return { id: k, name: k, dob: '', gender: '', picture: null };
  return {
    id: k?.id || k?._id || k?.childName || '',
    name: k?.childName || k?.name || '',
    dob: k?.birthDate || k?.dob ? String(k?.birthDate || k?.dob).slice(0, 10) : '',
    gender: k?.gender || '',
    picture: k?.imagePath || k?.picture || null,
  };
}

function normalizeMember(m) {
  const meta = m?.user_metadata || {};
  const nm = m?.networkMember || null;
  const name = meta.name || m?.name || 'Unknown';
  const joined = m?.joinedClasses || meta.joinedClasses || [];
  /* The joined networks this person is admin (or co-admin) of, from the API's
     adminNetworkIds: [{ id, name, type: 'class' | 'subGroup' }]. null when the
     API doesn't send that field yet — then nothing can say which networks they
     run, only that they run one (isNetworkAdmin). */
  const adminOf = Array.isArray(m?.adminNetworkIds)
    ? joined
        .filter((c) => m.adminNetworkIds.includes(c?.networkId))
        .map((c) => ({ id: c.networkId, name: c.name || '', type: c.networkType || '' }))
    : null;
  const rawKids = m?.childData || meta.childData || m?.kids || meta.kids || [];
  const children = (Array.isArray(rawKids) ? rawKids : []).map(normalizeKid).filter((k) => k.name);
  return {
    /* the auth0 userId — what the write APIs (email/role update) key on */
    id: m?.userId || m?.user_id || m?._id || m?.id || name,
    name,
    phone: meta.phoneNumber || m?.phoneNumber || m?.phone || '',
    email: meta.userEmail || meta.email || m?.userEmail || m?.email || nm?.email || '',
    picture: meta.profilePic || m?.profilePic || m?.photo || null,
    /* every joined network (classes + subgroups), comma-separated */
    group: joined.map((c) => c?.name).filter(Boolean).join(', ') || m?.groupName || m?.className || '',
    kids: children.map((k) => k.name),
    children,
    /* Status column: user_metadata.type 'Email only' → "Email only" chip;
       anything else — the key missing included — counts as on the app. */
    onSprout: String(meta.type || m?.type || '').toLowerCase() !== 'email only',
    /* Room Parent = admin (or co-admin) on THIS network's membership record. The
       top-level role is the user's global role and can disagree with it — when
       networkMember is present it alone decides the Access column. */
    isRoomParent: nm
      ? String(nm.role || '').toLowerCase() === 'admin' || nm.isCoAdmin === true
      : String(m?.role || meta.role || '').toLowerCase() === 'admin' ||
        !!(m?.isRoomParent || m?.roomParent || m?.isAdmin),
    /* Admin anywhere in the requested scope. getGroupMember computes the
       top-level role across every network it looked at, so on a type='all'
       (school-wide) call this is "room parent of at least one class here" —
       which the membership record alone can miss, since it is only the first of
       the several a user may hold. */
    isNetworkAdmin:
      String(m?.role || '').toLowerCase() === 'admin' ||
      (nm ? String(nm.role || '').toLowerCase() === 'admin' || nm.isCoAdmin === true : false),
    /* split by kind: classes = Room Parent of, sub-groups = Group Admin of.
       Both null alongside adminOf. */
    adminOf,
    adminClasses: adminOf && adminOf.filter((n) => n.type === 'class'),
    adminGroups: adminOf && adminOf.filter((n) => n.type === 'subGroup'),
  };
}

/* What a bulk-import file needs, mirroring the import service's header map and
   limits so the modal can say so up front. Headers are matched case- and
   punctuation-insensitively, so "Kid's Name" and "kidsname" both land. */
export const PARENT_IMPORT_COLUMNS = [
  { header: 'Name', aliases: 'or "Parent Name"', required: true },
  { header: 'Email', aliases: 'or "Email ID"', required: true },
  { header: 'Phone', aliases: 'or "Phone Number", "Mobile No"', required: true },
  { header: "Kid's Name", aliases: 'or "Kid Name", "Kids"', required: false },
  { header: 'Role', aliases: 'Parent or RoomParent — defaults to Parent', required: false },
];

export const PARENT_IMPORT_LIMITS = { extensions: '.csv, .xlsx, .xls', maxRows: 5000, maxSizeMb: 10 };

/* A row from /parents/:networkId. Each one is a person, assembled from up to
   two records: an app account (userId) and/or a roster record (parentId). A
   roster entry whose email or phone matches a member is folded into that
   member's row, so a converted parent appears once and carries both ids.
   Which id is present decides which API can write to them — see the notes on
   updateParent and updateMemberRole. */
function normalizeParent(p) {
  const parentId = p?.parentId ? String(p.parentId) : '';
  const userId = p?.userId ? String(p.userId) : '';
  return {
    /* row identity; either id may be absent, never both */
    id: parentId || userId,
    /* the roster record PUT /parent patches, '' when they were never on it */
    parentId,
    /* the app account the member APIs patch, '' when they haven't joined */
    userId,
    name: p?.name || '',
    kidsName: p?.kidsName || '',
    email: p?.email || '',
    phone: p?.phone || '',
    /* only someone on the app has an account to carry a photo, and it arrives
       as "" when they never set one — either way the avatar falls back to
       initials. */
    picture: p?.user_metadata?.profilePic || p?.profilePic || null,
    /* the roster record's own active/inactive flag — not the Status chip */
    status: p?.status || 'active',
    isRoomParent: String(p?.role || '') === 'RoomParent' || p?.isCoAdmin === true,
    /* the Status chip: the server decides who counts as on the app */
    onSprout: /on the app/i.test(String(p?.memberStatus || '')),
  };
}

const roomParentsService = {
  /* type 'all' = every member across a school's classes + subgroups (id = schoolId);
     type 'group' = members of one class/network (id = classId).
     Pagination is server-side; returns { members, total, totalPages }. */
  getGroupMembers: async (id, { type = 'all', page = 1, limit = 10, search = '' } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.GROUP_MEMBERS(id), {
      params: { type, page, limit, search },
    });
    const data = res?.data ?? res;
    const rawList =
      data?.members || data?.users || data?.docs || data?.items || data?.results || (Array.isArray(data) ? data : []);
    const total = Number(data?.total ?? data?.totalCount ?? data?.count ?? data?.pagination?.total ?? rawList.length);
    const totalPages = Number(
      data?.totalPages ?? data?.pages ?? data?.pagination?.totalPages ?? Math.max(1, Math.ceil(total / limit))
    );
    return { members: rawList.map(normalizeMember), total, totalPages };
  },

  /* Everyone with Room Parent access across a school, for the Master dashboard.
     There is no endpoint for it, so this walks the school-wide member list
     (type='all' = every class + sub-group) and keeps the admins. Capped at
     maxPages so a big school can't turn one card into 20 requests.

     With the API's adminNetworkIds (see adminClasses / adminGroups on each
     member) room parents come first and sub-group-only admins after. Without
     it, it's the flat isNetworkAdmin list it always was. */
  getSchoolRoomParents: async (schoolId, { limit = 100, maxPages = 5 } = {}) => {
    const first = await roomParentsService.getGroupMembers(schoolId, { type: 'all', page: 1, limit });
    let all = first.members;
    for (let p = 2; p <= Math.min(first.totalPages, maxPages); p++) {
      const next = await roomParentsService.getGroupMembers(schoolId, { type: 'all', page: p, limit });
      all = all.concat(next.members);
    }
    const admins = all.filter((m) => (m.adminOf ? m.adminOf.length > 0 : m.isNetworkAdmin));
    /* stable sort: keeps the API's name order inside each half */
    const isRp = (m) => (m.adminClasses ? m.adminClasses.length > 0 : true);
    return admins.sort((a, b) => Number(isRp(b)) - Number(isRp(a)));
  },

  /* Everyone in the class: app members and roster-only contacts merged into one
     row per person (see normalizeParent). Paginated and filtered server-side —
     `memberStatus` narrows to one side of the Status column. Returns
     { parents, summary: { total, onTheApp, emailOnly }, total, totalPages },
     where summary counts the whole network and ignores the filters. */
  getParents: async (networkId, { page = 1, limit = 10, search = '', memberStatus, status, role } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.PARENTS_BY_NETWORK(networkId), {
      params: {
        page,
        limit,
        search,
        ...(memberStatus ? { memberStatus } : {}),
        ...(status ? { status } : {}),
        ...(role ? { role } : {}),
      },
    });
    const data = res?.data ?? res;
    const rawList = data?.parents || (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? data?.total ?? rawList.length);
    const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit)));
    const summary = data?.summary || null;
    return { parents: rawList.map(normalizeParent), summary, total, totalPages };
  },

  /* The whole class roster in one array — every row the Parents page lists,
     app members and email-only contacts alike, sorted by name server-side. For
     pickers that must offer everyone (Fundraising's donor dropdown) rather than
     a page of them. Walks the pages up to maxPages, like getSchoolRoomParents. */
  getAllParents: async (networkId, { limit = 200, maxPages = 5 } = {}) => {
    const first = await roomParentsService.getParents(networkId, { page: 1, limit });
    let all = first.parents;
    for (let p = 2; p <= Math.min(first.totalPages, maxPages); p++) {
      const next = await roomParentsService.getParents(networkId, { page: p, limit });
      all = all.concat(next.parents);
    }
    return all;
  },

  /* Add a parent to a network. { networkId, name, kidsName, email,
     phone (E.164), role: 'Parent' | 'RoomParent' } */
  addParent: (parent) => apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.PARENT, parent),

  /* Patch one roster record — id is a row's parentId, NOT its userId. Takes any
     of name, kidsName, email, phone (E.164), role, status; omitted fields are
     left alone. For a row that also has a userId the listing reads name, email,
     phone and role off the app account instead, so only kidsName and status
     show up there — use updateUserEmail/updateMemberRole for the rest. */
  updateParent: (parent) => apiClient.put(ENDPOINTS.ROOM_PARENT_ADMIN.PARENT, parent),

  /* Delete a roster record, scoped to the network so an id from another class
     can't be removed. Takes a row's parentId — this removes the roster entry
     only, so it can't take someone off the app. */
  deleteParent: (networkId, parentId) =>
    apiClient.delete(ENDPOINTS.ROOM_PARENT_ADMIN.PARENT_DELETE(networkId, parentId)),

  /* Drop every membership this user holds on the network — takes a row's
     userId (auth0). Their roster record, if they have one, is untouched and
     would resurface as an "Email only" row, so removeParentEverywhere below is
     what the UI calls. */
  removeFromNetwork: (networkId, userId) =>
    apiClient.delete(ENDPOINTS.ROOM_PARENT_ADMIN.REMOVE_FROM_NETWORK(networkId, userId)),

  /* Take a listing row off the class, whichever records back it. A converted
     parent has both, and leaving either behind keeps them in the list. */
  removeParentEverywhere: async (networkId, { userId, parentId }) => {
    if (userId) await roomParentsService.removeFromNetwork(networkId, userId);
    if (parentId) await roomParentsService.deleteParent(networkId, parentId);
  },

  /* Bulk import from a CSV/Excel file. The server reads the headers itself
     (see PARENT_IMPORT_COLUMNS) and upserts on networkId + email, so
     re-importing a corrected file updates rather than duplicates.
     Content-Type must be cleared: axios serializes FormData to JSON when the
     request carries the client's default application/json, which would drop
     the file. */
  importParents: async (networkId, file) => {
    const form = new FormData();
    form.append('networkId', networkId);
    form.append('file', file);
    const res = await apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.PARENT_IMPORT, form, {
      headers: { 'Content-Type': undefined },
      timeout: 120000,
    });
    const data = res?.data ?? res;
    return {
      total: Number(data?.total ?? 0),
      created: Number(data?.createdCount ?? 0),
      updated: Number(data?.updatedCount ?? 0),
      failed: Number(data?.failCount ?? 0),
      /* [{ row, email, reason }] — row numbers count the header line */
      failures: Array.isArray(data?.failures) ? data.failures : [],
    };
  },

  /* Change a member's login email. userId is the auth0 id (e.g. "auth0|…"). */
  updateUserEmail: (userId, email) =>
    apiClient.patch(ENDPOINTS.ROOM_PARENT_ADMIN.USER_EMAIL, { userId, email }),

  /* Grant/revoke Room Parent access for someone on the app: sets isCoAdmin on
     the networkMembers record, which is where the listing reads a member row's
     role from. Writing role via updateParent would not move that chip. */
  updateMemberRole: (networkId, userId, isCoAdmin) =>
    apiClient.patch(ENDPOINTS.ROOM_PARENT_ADMIN.MEMBER_ROLE, { networkId, userId, isCoAdmin }),

  list: () => apiClient.get(ENDPOINTS.ROOM_PARENTS.BASE),
  grant: (access) => apiClient.post(ENDPOINTS.ROOM_PARENTS.BASE, access),
  revoke: (name) => apiClient.delete(ENDPOINTS.ROOM_PARENTS.BY_NAME(name)),
  activityLog: () => apiClient.get(ENDPOINTS.ROOM_PARENTS.ACTIVITY),
};

export default roomParentsService;
